import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { BoardFunnel } from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { FUNNEL_TYPE } from '../../../../enum/constants';

export interface LeadConversionResult {
  target_type: 'deal';
  deal_funnel_id: string;
}

type LeadConversionStep = 'action' | 'funnel';

interface LeadConversionAction {
  title: string;
  icon: string;
  action: () => void;
  disabled?: boolean;
}

@Component({
  selector: 'app-board-v3-lead-conversion',
  templateUrl: './lead-conversion.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class LeadConversionComponent implements OnInit {
  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() cardIds: string[] = [];

  @Output() close = new EventEmitter<LeadConversionResult | void>();

  step: LeadConversionStep = 'action';
  dealFunnels: BoardFunnel[] = [];
  isLoading = false;
  readonly dealFunnelControl = new FormControl<string | null>(null, Validators.required);

  readonly actions: LeadConversionAction[] = [
    {
      title: 'deal',
      icon: 'add_circle',
      action: () => this.selectCreateDeal(),
    },
    {
      title: 'contact',
      icon: 'person',
      action: () => {},
      disabled: true,
    },
    {
      title: 'company',
      icon: 'apartment',
      action: () => {},
      disabled: true,
    },
    {
      title: 'board_v3_lead_conversion_deal_contact',
      icon: 'group',
      action: () => {},
      disabled: true,
    },
    {
      title: 'board_v3_lead_conversion_deal_contact_company',
      icon: 'workspaces',
      action: () => {},
      disabled: true,
    },
    {
      title: 'board_v3_lead_conversion_deal_company',
      icon: 'domain_add',
      action: () => {},
      disabled: true,
    },
    {
      title: 'board_v3_lead_conversion_contact_company',
      icon: 'contacts',
      action: () => {},
      disabled: true,
    },
  ];

  ngOnInit(): void {
    this.isLoading = true;
    this.boardService
      .getFunnels({ type: FUNNEL_TYPE.DEAL })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(funnels => {
        this.dealFunnels = funnels ?? [];
        this.isLoading = false;
      });
  }

  get selectedDealFunnelId(): string | null {
    return this.dealFunnelControl.value;
  }

  get hasDealFunnels(): boolean {
    return !!this.dealFunnels.length;
  }

  get selectedCardsCount(): number {
    return this.cardIds.length;
  }

  get isBulkMode(): boolean {
    return this.selectedCardsCount > 1;
  }

  selectCreateDeal(): void {
    if (this.dealFunnels.length === 1) {
      this.dealFunnelControl.setValue(this.dealFunnels[0].id);
      this.confirm();
      return;
    }

    this.step = 'funnel';
  }

  backToAction(): void {
    this.step = 'action';
  }

  confirm(): void {
    this.dealFunnelControl.markAsTouched();
    const dealFunnelId = this.selectedDealFunnelId;
    if (!dealFunnelId || this.dealFunnelControl.invalid) return;

    this.close.emit({
      target_type: 'deal',
      deal_funnel_id: dealFunnelId,
    });
  }

  cancel(): void {
    this.close.emit();
  }
}
